import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrophy } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Navbar from '../components/Layout/Navbar';
import Timer from '../components/UI/Timer';
import QuestionCard from '../components/Quiz/QuestionCard';
import ScorePopup from '../components/Quiz/ScorePopup';
import Button from '../components/UI/Button';
import api from '../api/axios';
import useTimer from '../hooks/useTimer';
import useSound from '../hooks/useSound';
import { useAuth } from '../context/AuthContext';

const TOTAL_QUESTIONS = 20;
const TIME_PER_Q      = 20;

function shuffleOptions(question) {
  const options    = [...(question.options || question.reponses || [])];
  const correctIdx = question.reponseCorrecte ?? question.correctAnswer ?? 0;
  const correctText = options[correctIdx];
  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }
  return { ...question, options, reponseCorrecte: options.indexOf(correctText) };
}

// ── Écran de résultat ─────────────────────────────────────────────────────────
function ResultScreen({ score, correctCount, champName, onBack }) {
  const percentage = Math.round((correctCount / TOTAL_QUESTIONS) * 100);

  const getRating = () => {
    if (percentage >= 90) return { label: 'Légende',         color: '#f0b429', icon: '👑' };
    if (percentage >= 70) return { label: 'Excellent',        color: '#22c55e', icon: '⭐' };
    if (percentage >= 50) return { label: 'Bien joué',        color: '#3b82f6', icon: '👍' };
    return                       { label: 'Peut mieux faire', color: '#94a3b8', icon: '📚' };
  };

  const { label, color, icon } = getRating();

  return (
    <motion.div
      className="app-container flex flex-col min-h-screen px-5 pt-6 pb-8"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
    >
      {/* Badge tournoi */}
      <div className="text-center mb-2">
        <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
          style={{ background: 'rgba(240,180,41,0.15)', color: '#f0b429', fontFamily: "'Rajdhani', sans-serif" }}>
          ⚔️ PHASE DE GROUPES — {champName}
        </span>
      </div>

      {/* Icône + label */}
      <div className="flex flex-col items-center py-4">
        <motion.div
          initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
          className="text-5xl mb-2"
        >
          {icon}
        </motion.div>
        <h2 className="text-3xl font-black uppercase tracking-widest"
          style={{ fontFamily: "'Rajdhani', sans-serif", color }}>
          {label}
        </h2>
        <p className="text-white/40 text-xs mt-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          SCORE ENREGISTRÉ DANS TA POULE
        </p>
      </div>

      {/* Score */}
      <motion.div
        className="rounded-3xl p-5 border border-white/10 mb-4 text-center"
        style={{ background: 'rgba(0,0,0,0.4)' }}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
      >
        <p className="text-white/50 text-sm mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>SCORE FINAL</p>
        <motion.div
          className="text-6xl font-black text-qfl-gold"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
          initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.35, type: 'spring' }}
        >
          {score}
        </motion.div>
        <p className="text-white/40 text-sm mt-1">points</p>

        <div className="flex justify-around mt-5">
          <div className="flex flex-col items-center">
            <span className="text-2xl font-black text-green-400" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {correctCount}/{TOTAL_QUESTIONS}
            </span>
            <span className="text-white/40 text-xs">Correctes</span>
          </div>
          <div className="w-px bg-white/10" />
          <div className="flex flex-col items-center">
            <span className="text-2xl font-black text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {percentage}%
            </span>
            <span className="text-white/40 text-xs">Précision</span>
          </div>
        </div>
      </motion.div>

      {/* Info */}
      <div className="flex items-start gap-3 p-3 rounded-2xl mb-6"
        style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
        <FaTrophy className="text-green-400 flex-shrink-0 mt-0.5" size={15} />
        <p className="text-white/70 text-sm leading-relaxed">
          Ton score est enregistré dans ta poule. Les <strong className="text-white">2 premiers</strong> de chaque groupe accèdent à la phase éliminatoire.
        </p>
      </div>

      <Button variant="gold" size="lg" fullWidth onClick={onBack}>
        VOIR MON CLASSEMENT DE POULE
      </Button>
    </motion.div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────
function ChampionshipQuiz() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { playCorrect, playIncorrect, playTick, playVictory } = useSound();

  const [champName, setChampName]       = useState('Tournoi');
  const [questions, setQuestions]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [submitting, setSubmitting]     = useState(false);
  const [gameId, setGameId]             = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [revealed, setRevealed]         = useState(false);
  const [score, setScore]               = useState(0);
  const [streak, setStreak]             = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [showScorePopup, setShowScorePopup] = useState(false);
  const [lastPoints, setLastPoints]     = useState(25);
  const [gameOver, setGameOver]         = useState(false);

  const nextQuestionRef = useRef(null);
  const scoreRef        = useRef(0);
  const correctRef      = useRef(0);
  const streakRef       = useRef(0);
  const startedAtRef    = useRef(null);
  // Réponses dont l'appel /answer a échoué : renvoyées à la fin pour que le
  // serveur les revalide (le score de poule est désormais calculé par lui).
  const missedAnswersRef = useRef([]);

  // ── Charger les infos du tournoi + 20 questions ───────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        // Infos tournoi
        const { data: champData } = await api.get(`/championships/${id}`);
        const c = champData.championship;

        if (c.status !== 'group_stage') {
          toast.error('La phase de groupes n\'est pas active');
          navigate(`/championships/${id}`);
          return;
        }

        // Vérifier que le joueur est inscrit et n'a pas encore joué
        const me = champData.participants.find((p) => p.id === user?.id);
        if (!me) {
          toast.error('Tu n\'es pas inscrit à ce tournoi');
          navigate(`/championships/${id}`);
          return;
        }
        if (me.score !== null && me.score !== undefined) {
          toast.error('Tu as déjà joué ta phase de groupes');
          navigate(`/championships/${id}`);
          return;
        }

        setChampName(c.nom);

        // 20 questions solo, tirage équilibré par difficulté (35% facile / 35% moyen / 30% difficile)
        // pour que le classement reflète la précision plutôt qu'un hasard de tirage
        const { data: qData } = await api.post('/games/solo/start', { nbQuestions: TOTAL_QUESTIONS, balanced: true });
        setQuestions((qData.questions || []).map(shuffleOptions));
        setGameId(qData.gameId || null);
        // Chrono de la partie : sert à départager deux joueurs à égalité de score
        startedAtRef.current = Date.now();
      } catch (err) {
        toast.error('Impossible de charger le quiz');
        navigate(`/championships/${id}`);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]);

  // ── Timer ─────────────────────────────────────────────────────────────────
  const handleTimeUp = useCallback(() => {
    if (!revealed) {
      playIncorrect();
      setRevealed(true);
      setSelectedAnswer(null);
      streakRef.current = 0;
      setStreak(0);
    }
  }, [revealed]);

  const { timeLeft, start, pause, reset } = useTimer(TIME_PER_Q, handleTimeUp);

  useEffect(() => {
    if (!loading && questions.length > 0 && !gameOver) {
      reset(TIME_PER_Q);
      start();
    }
  }, [loading, currentIndex, gameOver]);

  useEffect(() => {
    if (timeLeft <= 5 && timeLeft > 0 && !revealed) playTick();
  }, [timeLeft, revealed]);

  // ── Soumettre le score final ──────────────────────────────────────────────
  const submitFinal = useCallback(async (finalScore, finalCorrect) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/championships/${id}/group-score`, {
        gameId,
        score:           finalScore,
        correct_answers: finalCorrect,
        total_questions: TOTAL_QUESTIONS,
        time_ms:         startedAtRef.current ? Date.now() - startedAtRef.current : 0,
        missedAnswers:   missedAnswersRef.current,
      });
      if (data.streakCheckIn) {
        toast.success(`+${data.streakCheckIn.coinsEarned} pièces — Streak jour ${data.streakCheckIn.streakDays} ! 🔥`);
      }
      if (data.streakCheckedInToday) {
        updateUser({
          ...user,
          streak_checked_in_today: true,
          ...(data.streakDays !== undefined ? { streak_days: data.streakDays } : {}),
        });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || '';
      if (!msg.includes('déjà joué')) {
        toast.error('Erreur lors de l\'enregistrement du score');
      }
    } finally {
      setSubmitting(false);
      playVictory();
      setGameOver(true);
    }
  }, [id, gameId, user, updateUser]);

  // ── Répondre ──────────────────────────────────────────────────────────────
  const handleAnswer = useCallback(async (answerIndex) => {
    if (revealed || selectedAnswer !== null) return;

    pause();
    setSelectedAnswer(answerIndex);
    setRevealed(true);

    const q = questions[currentIndex];
    const correctIdx = q.reponseCorrecte ?? q.correctAnswer ?? 0;
    const isCorrect  = answerIndex === correctIdx;

    if (isCorrect) {
      const streakBonus = Math.min(streakRef.current, 4);
      const timeBonus   = Math.floor((timeLeft / TIME_PER_Q) * 10);
      const points      = 25 + streakBonus * 5 + timeBonus;
      scoreRef.current += points;
      correctRef.current += 1;
      streakRef.current += 1;
      setScore(scoreRef.current);
      setCorrectCount(correctRef.current);
      setStreak(streakRef.current);
      setLastPoints(points);
      setShowScorePopup(true);
      playCorrect();
      setTimeout(() => setShowScorePopup(false), 1200);
    } else {
      streakRef.current = 0;
      setStreak(0);
      playIncorrect();
    }

    // Enregistrer la réponse (non bloquant)
    if (gameId) {
      // shuffleOptions mélange q.options pour l'affichage ; bonne_reponse côté
      // serveur reste relatif à q.choix (ordre d'origine) — on reconvertit
      // l'index avant envoi, sinon le serveur invalide la quasi-totalité des
      // bonnes réponses (bug trouvé le 23/10/2026).
      const originalChoix   = Array.isArray(q.choix) ? q.choix : q.options;
      const serverAnswerIdx = originalChoix.indexOf(q.options[answerIndex]);
      const payload = {
        gameId,
        questionId: q._id || q.id,
        answer:     serverAnswerIdx,
        timeMs:     (TIME_PER_Q - timeLeft) * 1000,
        currentStreak: streakRef.current,
      };
      api.post('/games/solo/answer', payload).catch(() => {
        missedAnswersRef.current.push({
          questionId: payload.questionId,
          answer:     payload.answer,
          timeMs:     payload.timeMs,
        });
      });
    }
  }, [revealed, selectedAnswer, questions, currentIndex, timeLeft, pause, gameId]);

  // ── Passer à la question suivante ─────────────────────────────────────────
  const nextQuestion = useCallback(() => {
    if (currentIndex + 1 >= TOTAL_QUESTIONS || currentIndex + 1 >= questions.length) {
      pause();
      submitFinal(scoreRef.current, correctRef.current);
    } else {
      setCurrentIndex((i) => i + 1);
      setSelectedAnswer(null);
      setRevealed(false);
    }
  }, [currentIndex, questions.length, pause, submitFinal]);

  nextQuestionRef.current = nextQuestion;

  // ── Écrans ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="app-container flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="w-16 h-16 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
        <p className="text-qfl-gold font-bold tracking-widest text-sm"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          CHARGEMENT DU QUIZ...
        </p>
      </div>
    );
  }

  if (gameOver) {
    return (
      <ResultScreen
        score={score}
        correctCount={correctCount}
        champName={champName}
        onBack={() => navigate(`/championships/${id}`)}
      />
    );
  }

  if (submitting) {
    return (
      <div className="app-container flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="w-16 h-16 rounded-full border-4 border-green-400 border-t-transparent animate-spin" />
        <p className="text-green-400 font-bold tracking-widest text-sm"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          ENREGISTREMENT DU SCORE...
        </p>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: 20 }}>
      <Navbar
        showBack
        onBack={() => {
          if (window.confirm('Quitter le quiz ? Ton score ne sera pas enregistré.')) {
            navigate(`/championships/${id}`);
          }
        }}
        title={champName}
      />

      {/* Bandeau tournoi */}
      <div className="mx-4 mt-2 mb-1 flex items-center justify-between px-3 py-1.5 rounded-xl"
        style={{ background: 'rgba(240,180,41,0.08)', border: '1px solid rgba(240,180,41,0.2)' }}>
        <span className="text-qfl-gold text-xs font-bold uppercase tracking-widest"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          ⚔️ PHASE DE GROUPES
        </span>
        <span className="text-white/40 text-xs"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          {currentIndex + 1}/{TOTAL_QUESTIONS}
        </span>
      </div>

      {/* Timer */}
      <div className="flex justify-center py-3 relative">
        <Timer timeLeft={timeLeft} totalTime={TIME_PER_Q} size={90} />
        {streak > 1 && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}
            className="absolute right-6 top-3 flex flex-col items-center"
          >
            <span className="text-orange-400 text-2xl">🔥</span>
            <span className="text-orange-400 font-bold text-xs"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}>x{streak}</span>
          </motion.div>
        )}
      </div>

      {/* Question */}
      <div className="flex-1 px-4 overflow-y-auto pb-4 relative">
        <AnimatePresence mode="wait">
          <QuestionCard
            key={currentIndex}
            question={currentQuestion}
            questionIndex={currentIndex}
            totalQuestions={TOTAL_QUESTIONS}
            selectedAnswer={selectedAnswer}
            correctAnswer={currentQuestion?.reponseCorrecte ?? currentQuestion?.correctAnswer}
            revealed={revealed}
            onAnswer={handleAnswer}
            disabled={revealed}
            eliminatedOptions={[]}
          />
        </AnimatePresence>

        <ScorePopup points={lastPoints} visible={showScorePopup} streakBonus={streak > 2} />
      </div>

      {/* Bouton continuer */}
      <AnimatePresence>
        {revealed && (
          <motion.div className="px-4 py-3"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}>
            <Button variant="gold" size="lg" fullWidth onClick={nextQuestion}>
              {currentIndex + 1 >= TOTAL_QUESTIONS
                ? 'VOIR MON SCORE'
                : 'CONTINUER'}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ChampionshipQuiz;
